import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
    Star, 
    Quote,
    Users,
    MessageCircle,
    Phone,
    Video,
    Plane,
    Award,
    ChevronDown
} from 'lucide-react';

// Review categories with counts totaling 722
// Community: 83, CJO Success: 75, 1-on-1 Coaching: 121, Mentorship: 180, Book: 143, Course: 120

const reviewTemplates = {
    coaching: [
        "One-on-one coaching identified exactly what I was doing wrong.",
        "Shelly's coaching feedback was incredibly specific and helpful.",
        "The mock interview in coaching prepared me perfectly!",
        "After coaching, I walked into my interview with confidence.",
        "Coaching tips I couldn't find anywhere else online.",
        "Shelly knew exactly what the airline was looking for!",
        "Best money spent on my flight attendant journey.",
        "Coaching session the night before calmed my nerves.",
        "Shelly's coaching transformed my interview skills overnight.",
        "Got my CJO two weeks after my coaching session!",
        "Coaching covered everything from appearance to tough questions.",
        "Direct feedback during coaching was eye-opening.",
        "After 3 failed interviews, coaching showed me what was wrong.",
        "Personalized attention in coaching was amazing.",
        "Shelly helped me craft my personal story perfectly.",
        "Coaching made my answers feel natural, not rehearsed.",
        "The hour flew by but I learned so much!",
        "Coaching feedback led directly to my CJO!",
        "Shelly caught issues I never would have noticed.",
        "Worth every penny - coaching changed everything!",
        "Finally got my dream job after coaching session!",
        "Coaching helped me understand the interview process.",
        "Shelly's insider knowledge is unmatched.",
        "One coaching session turned my journey around.",
        "I recommend coaching to everyone serious about this career.",
        "The personalized approach made all the difference.",
        "Coaching gave me the edge I needed to succeed.",
        "Shelly's experience really shows in her coaching.",
        "Best investment I've made in my career.",
        "The mock interview practice was invaluable.",
    ],
    community: [
        "The community support kept me going through rejections.",
        "Weekly live Q&As with Coach Shelly are incredibly valuable!",
        "Found my interview buddy in the community!",
        "The community celebrates every CJO together!",
        "Shelly answers questions in the community daily.",
        "Best support system during my application process.",
        "The community tips helped me nail my video interview.",
        "Everyone is so supportive and encouraging!",
        "The community is worth the membership alone.",
        "Made lifelong friends in Shelly's community.",
        "The shared experiences helped me so much.",
        "Community members share real interview questions.",
        "I never felt alone in my journey thanks to this group.",
        "The encouragement kept me motivated.",
        "Weekly discussions are always so informative.",
        "Love the positive energy in this community!",
        "The community helped me stay focused.",
        "Best decision joining this supportive group.",
        "Everyone genuinely wants you to succeed.",
        "The community is like having a flight attendant family.",
    ],
    cjo: [
        "I can't believe I'm saying this but I GOT MY CJO!",
        "After years of trying, I finally got my CJO!",
        "CJO RECEIVED! Shelly's methods work!",
        "Got my CJO at my dream airline!",
        "From nervous applicant to CJO holder!",
        "Got my CJO today! Following Shelly's advice worked!",
        "CJO!!! After 5 failed attempts, I finally made it!",
        "I received my CJO last week!",
        "Got my CJO! The investment was worth it!",
        "CJO in hand! Best career decision ever!",
        "I'm officially going to be a flight attendant!",
        "CJO RECEIVED! Dreams do come true!",
        "United CJO! I felt so prepared!",
        "Delta has been my dream - finally have my CJO!",
        "Got my CJO! Worth every penny!",
        "CJO holder here! Thank you Shelly!",
        "From 0 callbacks to CJO!",
        "CJO achieved! Never give up on your dreams!",
        "Living my dream! CJO received!",
        "CJO! Shelly believed in me when I didn't!",
        "Received my CJO! The guidance was incredible!",
        "Got my CJO with a brand new airline!",
        "CJO! After being furloughed, I'm back!",
        "My third attempt and I FINALLY got my CJO!",
        "CJO! The mock interview was key!",
    ],
    mentorship: [
        "The mentorship program completely transformed my interview skills!",
        "Shelly's mentorship gave me the confidence I needed to succeed.",
        "Best investment in my career - got my CJO after the mentorship!",
        "The personalized roadmap in mentorship was exactly what I needed.",
        "Mentorship mock interviews prepared me perfectly for the real thing.",
        "Shelly's mentorship helped me land my dream job!",
        "The STAR method training in mentorship was a game-changer.",
        "6 mock interviews later, I walked into my F2F with total confidence.",
        "Mentorship feedback on my body language made all the difference.",
        "Shelly's grooming and appearance guidance was incredibly detailed.",
        "The video intro review in mentorship helped me stand out!",
        "Unlimited text support during mentorship calmed my nerves.",
        "Dress rehearsal before my interview was worth every penny.",
        "Mentorship taught me exactly what airlines are looking for.",
        "The post-interview debrief helped me prepare for training!",
        "Shelly's airline-specific prep in mentorship was incredible.",
        "From nervous wreck to confident candidate - thanks mentorship!",
        "The 4-week mentorship plan fit perfectly with my schedule.",
        "Breathing exercises and visualization changed my mindset.",
        "Mentorship resume review got me more callbacks!",
        "I nailed my panel interview thanks to mentorship prep!",
        "Worth way more than the price - comprehensive and effective.",
        "Shelly genuinely cared about my success in mentorship.",
        "The scenario role-playing in mentorship was so helpful!",
        "Mentorship group assessment tips gave me an edge.",
        "Finally understood what I was doing wrong after mentorship.",
        "The virtual wardrobe check was such a nice touch!",
        "Psychometric test prep in mentorship saved me!",
        "Got my CJO on first try after completing mentorship!",
        "Mentorship was the best decision of my career journey.",
        "The intensive practice sessions were exactly what I needed.",
        "Mentorship gave me clarity on my weaknesses.",
        "Every session was packed with valuable information.",
        "The structured approach made learning so much easier.",
        "Mentorship covered every possible interview scenario.",
    ],
    book: [
        "The book was my bible during the application process!",
        "Every tip in the book was spot-on and relevant.",
        "Interview chapter alone is worth 10x the price!",
        "Got hired on my first try following the book's advice.",
        "The book covers everything from resume to what to wear.",
        "Shelly's insider knowledge shines through in every page.",
        "Read it three times before my interview - so helpful!",
        "The book gave me confidence I didn't have before.",
        "Airport codes tricks helped me in training!",
        "Situational question answers in the book saved me!",
        "Best flight attendant prep book out there.",
        "The book's grooming tips were so detailed!",
        "I highlighted almost every page - that helpful!",
        "Clear, concise, and straight to the point.",
        "The book helped me understand what airlines want.",
        "Instant download was so convenient!",
        "Referred the book to all my friends applying.",
        "The practice questions were exactly what they asked!",
        "Worth way more than $29.99!",
        "The book started my journey to my dream job.",
        "Easy to read and packed with useful information.",
        "The book gave me the foundation I needed.",
        "Every aspiring FA should read this book!",
        "The tips are practical and actionable.",
        "This book made the application process less scary.",
        "Shelly's experience comes through in every chapter.",
        "The book answered all my questions about the industry.",
        "Best purchase for my flight attendant journey.",
        "The book is comprehensive and well-organized.",
        "Reading this book was the first step to my CJO.",
    ],
    course: [
        "The course videos were easy to follow and so helpful!",
        "Loved the appearance and assessment video!",
        "The resume video helped me get more callbacks.",
        "Each video was under 10 minutes - perfect for busy schedule.",
        "Course content prepared me for every interview stage.",
        "The interview questions video was a lifesaver!",
        "Training video helped me know what to expect.",
        "Worth $297 - comprehensive and professional.",
        "I watched the videos multiple times before my F2F.",
        "Course gave me an edge over other candidates.",
        "The application process video saved me so much time.",
        "Lifetime access means I can always refresh my knowledge.",
        "The course + book combo is unbeatable!",
        "Nervousness control tips from the course worked!",
        "Course videos are high quality and informative.",
        "The visual learning format really helped me.",
        "Course content was exactly what I needed.",
        "Each module built on the previous one perfectly.",
        "The course structure made learning easy.",
        "Best online course for aspiring flight attendants.",
        "The bonus content was an unexpected treat!",
        "Course helped me feel prepared and confident.",
        "Shelly explains everything so clearly in the videos.",
        "The course was worth every dollar.",
        "I recommend this course to everyone applying.",
    ]
};

// Generate names
const names = ["Jessica", "Amanda", "Taylor", "Morgan", "Brittany", "Kayla", "Rachel", "Nicole", "Lauren", "Ashley", "Samantha", "Megan", "Emily", "Stephanie", "Christina", "Sarah", "Jennifer", "Amber", "Lindsay", "Melissa", "Andrea", "Rebecca", "Crystal", "Vanessa", "Patricia", "Diana", "Maria", "Catherine", "Elizabeth", "Victoria", "Angela", "Natalie", "Monica", "Julia", "Sophia", "Grace", "Hannah", "Olivia", "Isabella", "Emma", "Ava", "Mia", "Charlotte", "Amelia", "Harper", "Evelyn", "Abigail", "Ella", "Madison", "Chloe", "Danielle", "Heather", "Michelle", "Tiffany", "Courtney", "Kimberly", "Alexandria", "Brianna", "Jasmine", "Gabrielle", "Allison", "Katherine", "Brooke", "Haley", "Kelly", "Paige", "Savannah", "Alexis", "Kaylee", "Sydney"];
const lastInitials = ["M.", "R.", "S.", "K.", "L.", "W.", "P.", "T.", "H.", "C.", "B.", "G.", "F.", "A.", "N.", "D.", "J.", "V.", "E.", "O."];
const airlines = ["United Airlines", "Delta Air Lines", "American Airlines", "Southwest Airlines", "JetBlue", "Alaska Airlines", "Hawaiian Airlines", "Spirit Airlines", "Frontier Airlines", "Allegiant Air", "Breeze Airways", "Sun Country"];

// Generate all reviews with specific counts
const generateReviews = () => {
    const reviews = [];
    const counts = {
        coaching: 121,
        community: 83,
        cjo: 75,
        mentorship: 180,
        book: 143,
        course: 120
    };
    
    let nameIndex = 0;
    
    Object.entries(counts).forEach(([type, count]) => {
        const templates = reviewTemplates[type];
        for (let i = 0; i < count; i++) {
            const templateIndex = i % templates.length;
            reviews.push({
                name: `${names[nameIndex % names.length]} ${lastInitials[nameIndex % lastInitials.length]}`,
                airline: airlines[nameIndex % airlines.length],
                rating: Math.random() > 0.15 ? 5 : 4, // 85% 5-star, 15% 4-star
                type: type,
                text: templates[templateIndex]
            });
            nameIndex++;
        }
    });
    
    // Shuffle reviews
    for (let i = reviews.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [reviews[i], reviews[j]] = [reviews[j], reviews[i]];
    }
    
    return reviews;
};

const allReviews = generateReviews();

export default function Reviews() {
    const navigate = useNavigate();
    const [filter, setFilter] = useState('all');
    const [showAll, setShowAll] = useState(false);
    
    const filteredReviews = filter === 'all' 
        ? allReviews 
        : allReviews.filter(r => r.type === filter);
    
    const displayedReviews = showAll ? filteredReviews : filteredReviews.slice(0, 12);
    
    // Review counts by category
    const reviewCounts = {
        coaching: 121,
        community: 83,
        cjo: 75,
        mentorship: 180,
        book: 143,
        course: 120
    };
    
    const stats = {
        total: 722,
        avgRating: 4.9,
        fiveStars: Math.round(722 * 0.85),
        cjos: '1000+'
    };

    const filterButtons = [
        { key: 'all', label: 'All Reviews', icon: Star, count: 722 },
        { key: 'coaching', label: '1-on-1 Coaching', icon: Phone, count: 121 },
        { key: 'community', label: 'Community', icon: MessageCircle, count: 83 },
        { key: 'cjo', label: 'CJO Success', icon: Award, count: 75 },
        { key: 'mentorship', label: 'Mentorship', icon: Users, count: 180 },
        { key: 'book', label: 'Book', icon: Quote, count: 143 },
        { key: 'course', label: 'Course', icon: Video, count: 120 },
    ];

    return (
        <div className="min-h-screen">
            {/* Hero Section */}
            <section className="py-16 bg-gradient-to-b from-primary/5 to-background">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-center max-w-3xl mx-auto"
                    >
                        <Badge variant="secondary" className="mb-4">
                            <Star className="h-3 w-3 mr-1 fill-yellow-400 text-yellow-400" />
                            Student Reviews
                        </Badge>
                        
                        <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-4">
                            Real Stories, Real Success
                        </h1>
                        
                        <p className="text-lg text-muted-foreground mb-8">
                            Join over <strong className="text-primary">1,000+ future flight attendants</strong> who have received their CJO with Shelly's help.
                        </p>

                        {/* Stats */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
                            <Card className="p-4 text-center">
                                <p className="text-3xl font-bold text-primary">{stats.cjos}</p>
                                <p className="text-sm text-muted-foreground">CJOs Achieved</p>
                            </Card>
                            <Card className="p-4 text-center">
                                <p className="text-3xl font-bold text-primary">{stats.avgRating}</p>
                                <p className="text-sm text-muted-foreground">Average Rating</p>
                            </Card>
                            <Card className="p-4 text-center">
                                <p className="text-3xl font-bold text-primary">{stats.fiveStars}</p>
                                <p className="text-sm text-muted-foreground">5-Star Reviews</p>
                            </Card>
                            <Card className="p-4 text-center">
                                <p className="text-3xl font-bold text-primary">40+</p>
                                <p className="text-sm text-muted-foreground">Airlines</p>
                            </Card>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Filter Tabs */}
            <section className="py-8 border-b sticky top-0 bg-background z-10">
                <div className="container mx-auto px-4">
                    <div className="flex flex-wrap justify-center gap-2">
                        {filterButtons.map((btn) => (
                            <Button
                                key={btn.key}
                                variant={filter === btn.key ? "default" : "outline"}
                                className="rounded-full gap-2"
                                onClick={() => {
                                    setFilter(btn.key);
                                    setShowAll(false);
                                }}
                            >
                                <btn.icon className="h-4 w-4" />
                                {btn.label}
                                {btn.key !== 'all' && (
                                    <Badge variant="secondary" className="ml-1 text-xs">
                                        {allReviews.filter(r => r.type === btn.key).length}
                                    </Badge>
                                )}
                            </Button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Reviews Grid */}
            <section className="py-12">
                <div className="container mx-auto px-4">
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {displayedReviews.map((review, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.05 }}
                            >
                                <Card className="p-6 h-full flex flex-col">
                                    {/* Header */}
                                    <div className="flex items-start justify-between mb-4">
                                        <div>
                                            <p className="font-semibold">{review.name}</p>
                                            <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                                <Plane className="h-3 w-3" />
                                                <span>{review.airline}</span>
                                            </div>
                                        </div>
                                        <Badge 
                                            variant="outline" 
                                            className={
                                                review.type === 'coaching' ? 'border-blue-500 text-blue-600' :
                                                review.type === 'community' ? 'border-purple-500 text-purple-600' :
                                                review.type === 'cjo' ? 'border-green-500 text-green-600' :
                                                review.type === 'mentorship' ? 'border-amber-500 text-amber-600' :
                                                review.type === 'book' ? 'border-pink-500 text-pink-600' :
                                                'border-cyan-500 text-cyan-600'
                                            }
                                        >
                                            {review.type === 'coaching' ? '1-on-1' : 
                                             review.type === 'community' ? 'Community' :
                                             review.type === 'cjo' ? 'CJO' :
                                             review.type === 'mentorship' ? 'Mentorship' :
                                             review.type === 'book' ? 'Book' : 'Course'}
                                        </Badge>
                                    </div>

                                    {/* Stars */}
                                    <div className="flex gap-0.5 mb-3">
                                        {[...Array(5)].map((_, i) => (
                                            <Star 
                                                key={i} 
                                                className={`h-4 w-4 ${i < review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-200'}`} 
                                            />
                                        ))}
                                    </div>

                                    {/* Review Text */}
                                    <div className="flex-1">
                                        <Quote className="h-6 w-6 text-primary/20 mb-2" />
                                        <p className="text-muted-foreground text-sm leading-relaxed">
                                            {review.text}
                                        </p>
                                    </div>
                                </Card>
                            </motion.div>
                        ))}
                    </div>

                    {/* Load More Button */}
                    {!showAll && filteredReviews.length > 12 && (
                        <div className="text-center mt-8">
                            <Button 
                                variant="outline" 
                                size="lg"
                                className="rounded-full gap-2"
                                onClick={() => setShowAll(true)}
                            >
                                <ChevronDown className="h-4 w-4" />
                                Show All {filteredReviews.length} Reviews
                            </Button>
                        </div>
                    )}
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-16 bg-primary/5">
                <div className="container mx-auto px-4 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="font-heading text-3xl font-bold mb-4">
                            Ready to Get Your CJO?
                        </h2>
                        <p className="text-muted-foreground max-w-xl mx-auto mb-8">
                            Join the 1,000+ successful flight attendants who started their journey with Shelly's help.
                        </p>
                        <div className="flex flex-wrap justify-center gap-4">
                            <Button 
                                size="lg" 
                                className="rounded-full gap-2"
                                onClick={() => navigate('/coaching')}
                            >
                                <Phone className="h-5 w-5" />
                                Book 1-on-1 Coaching
                            </Button>
                            <Button 
                                size="lg" 
                                variant="outline"
                                className="rounded-full gap-2"
                                onClick={() => navigate('/membership')}
                            >
                                <Users className="h-5 w-5" />
                                Join Community
                            </Button>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
}
